import { entities, toJS, types, type UserAccountType } from "@fraction/shared";
import {
  APPLICATION_KEY,
  liteApplicationKey,
  liteMerge,
  SINGLE_APPLICATION_KEY,
  seedApplicationFromList,
  updateApplicationInFetchCache as sharedUpdateApplicationInFetchCache,
  updateEntityFieldInFetchCache,
  useApplicationMutations as useSharedApplicationMutations,
} from "@fraction/shared-frontend";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import fraction, { type ChecklistApp } from "src/api/fraction";
import { DOCUMENTS_KEY } from "src/apps/PostFundedDashboard/queryKeys";
import { useUpdateLocalUser } from "src/auth/useAuth";
import { useCachedQuery } from "src/hooks/useCache";
import { type UseQueryOptions, useQuery } from "src/lib";
import {
  publishApplicationCacheChange,
  publishCrossWindowCacheChange,
} from "src/lib/runtime/crossWindowCache";
import { isSearchablePipelineApp, useSearchableAppsStore } from "src/stores/searchableAppsStore";
import { useSelectedApplicationStore } from "src/stores/selectedApplicationStore";
import { useStableSelect } from "./useStableSelect";

// Application cache keys + the optimistic cache-writer are the single source of
// truth in @fraction/shared-frontend (shared with mobile). Re-exported here so
// the many web consumers that import them from `src/hooks/useApplication` keep
// working unchanged.
export { APPLICATION_KEY, liteApplicationKey, SINGLE_APPLICATION_KEY, updateEntityFieldInFetchCache };

/**
 * The shared application-cache writer, plus a note to the other native windows.
 *
 * Wrapped here rather than in `@fraction/shared-frontend`, which mobile also loads and where none
 * of this exists. Every web call site already imports it from this module, so wrapping the
 * re-export covers all of them without touching one.
 */
export const updateApplicationInFetchCache: typeof sharedUpdateApplicationInFetchCache = (
  queryClient,
  applicationId,
  updater
) => {
  const result = sharedUpdateApplicationInFetchCache(queryClient, applicationId, updater);
  publishApplicationCacheChange(applicationId);
  return result;
};

function updateFileInArray(
  files: entities.UploadedFile[] | undefined | null,
  documentId: string,
  updater: (prev: entities.UploadedFile) => entities.UploadedFile
): entities.UploadedFile[] | undefined | null {
  if (!files) return files;
  return files.map((f) => (f.id === documentId ? updater(f) : f));
}

function updateFilesInOwnable(
  ownable: { files?: entities.UploadedFile[] | null } | undefined | null,
  documentId: string,
  updater: (prev: entities.UploadedFile) => entities.UploadedFile
) {
  if (!ownable?.files) return ownable;
  return { ...ownable, files: updateFileInArray(ownable.files, documentId, updater) };
}

function updateDocumentInApplication(
  app: ChecklistApp,
  documentId: string,
  updater: (prev: entities.UploadedFile) => entities.UploadedFile
): ChecklistApp {
  return {
    ...app,
    files: updateFileInArray(app.files, documentId, updater),
    applicants: app.applicants?.map((ap) => ({
      ...ap,
      files: updateFileInArray(ap?.files, documentId, updater),
      incomes: ap?.incomes?.map((x) => updateFilesInOwnable(x, documentId, updater)),
      debts: ap?.debts?.map((x) => updateFilesInOwnable(x, documentId, updater)),
      assets: ap?.assets?.map((x) => updateFilesInOwnable(x, documentId, updater)),
      properties: ap?.properties?.map((p) => ({
        ...p,
        debts: p?.debts?.map((x) => updateFilesInOwnable(x, documentId, updater)),
        incomes: p?.incomes?.map((x) => updateFilesInOwnable(x, documentId, updater)),
      })),
      homeAddress: ap?.homeAddress
        ? {
            ...ap.homeAddress,
            debts: ap.homeAddress?.debts?.map((x) => updateFilesInOwnable(x, documentId, updater)),
            incomes: ap.homeAddress?.incomes?.map((x) => updateFilesInOwnable(x, documentId, updater)),
          }
        : ap?.homeAddress,
    })),
    property: app.property
      ? {
          ...app.property,
          debts: app.property?.debts?.map((x) => updateFilesInOwnable(x, documentId, updater)),
          incomes: app.property?.incomes?.map((x) => updateFilesInOwnable(x, documentId, updater)),
        }
      : app.property,
  } as ChecklistApp;
}

export const updateDocumentInFetchCache = (
  queryClient: QueryClient,
  documentId: string,
  applicationId: string,
  rawUpdater: (prev: entities.UploadedFile) => entities.UploadedFile
) => {
  /**
   * What the document became, captured on its way past so the other native windows can be told.
   *
   * Which COPY is captured matters, because they are not equally complete. The list caches carry
   * the document as the documents endpoint returns it, presigned `s3Document.href` included; the
   * application graph carries a thinner one built by the deal fetcher, with no href on it. Publish
   * the graph's copy and the receiving window replaces a full row with a thinner one — the document
   * stays in the list and loses its Preview button, which is exactly what it did.
   *
   * So the list copy wins, and the graph's is only a fallback for a document that no list in this
   * window happens to hold. The checklist branch is excluded outright: it runs the same updater
   * against a synthetic stub of an id, a status and notes, and publishing that would overwrite a
   * whole document elsewhere with three fields.
   */
  let listDocument: entities.UploadedFile | undefined;
  let graphDocument: entities.UploadedFile | undefined;
  const updater = (prev: entities.UploadedFile) => {
    const next = rawUpdater(prev);
    listDocument ??= next;
    return next;
  };
  const graphUpdater = (prev: entities.UploadedFile) => {
    const next = rawUpdater(prev);
    graphDocument ??= next;
    return next;
  };

  // 1. Update main documents list
  queryClient.setQueriesData<entities.UploadedFile[]>(
    { exact: false, queryKey: [...DOCUMENTS_KEY, applicationId] },
    (prev) => prev?.map((doc) => (doc.id === documentId ? updater(doc) : doc))
  );

  // 2. Update local-only documents cache
  queryClient.setQueriesData<entities.UploadedFile[]>(
    { exact: false, queryKey: [...DOCUMENTS_KEY, applicationId, "local"] },
    (prev) => prev?.map((doc) => (doc.id === documentId ? updater(doc) : doc))
  );

  // 3. Update documents needing approval
  queryClient.setQueriesData<entities.UploadedFile[]>(
    { exact: false, queryKey: ["documents", "needs-approval"] },
    (prev) => prev?.map((doc) => (doc.id === documentId ? updater(doc) : doc))
  );

  // 4. Update individual file cache
  queryClient.setQueriesData<entities.UploadedFile>(
    { exact: false, queryKey: ["initial-file", documentId] },
    (prev) => (prev ? updater(prev) : prev)
  );

  // 5. Update document nested within all application cache locations (including embedded checklists)
  const updateChecklistDocuments = (
    checklist: types.ChecklistResult[] | undefined
  ): types.ChecklistResult[] | undefined => {
    if (!checklist) return checklist;
    return checklist.map((item) => {
      if (!types.isDocumentChecklistItem(item)) return item;

      const matchesPrimary = item.id === documentId;
      const matchesInDocuments = item.documents?.some((doc) => doc.id === documentId);
      if (!matchesPrimary && !matchesInDocuments) return item;

      const updatedFile = rawUpdater(
        new entities.UploadedFile({ id: documentId, status: item.approval, notes: item.notes })
      );

      return {
        ...item,
        ...(matchesPrimary && {
          approval: updatedFile.status ?? item.approval,
          notes: updatedFile.notes ?? item.notes,
        }),
        ...(matchesInDocuments &&
          item.documents && {
            documents: item.documents.map((doc) => {
              if (doc.id !== documentId) return doc;
              return {
                ...doc,
                approval: updatedFile.status ?? doc.approval,
                notes: updatedFile.notes ?? doc.notes,
              };
            }),
          }),
      };
    });
  };

  updateApplicationInFetchCache(queryClient, applicationId, (prev) => {
    if (!prev) return prev;
    const updated = updateDocumentInApplication(prev, documentId, graphUpdater);
    if (updated?.checklists) {
      return {
        ...updated,
        checklists: Object.fromEntries(
          Object.entries(updated.checklists).map(([status, checklist]) => [
            status,
            updateChecklistDocuments(checklist as types.ChecklistResult[]) || checklist,
          ])
        ),
      } as ChecklistApp;
    }
    return updated;
  });

  // 6. Update standalone checklist caches
  queryClient.setQueriesData<types.ChecklistResult[]>(
    { exact: false, queryKey: ["checklist", ...APPLICATION_KEY, applicationId] },
    (prev) => {
      if (!prev || !Array.isArray(prev)) return prev;
      return updateChecklistDocuments(prev);
    }
  );

  type ChecklistsRecord = Partial<Record<string, types.ChecklistResult[]>>;
  queryClient.setQueryData<ChecklistsRecord>(
    ["checklist", ...APPLICATION_KEY, applicationId],
    (prev): ChecklistsRecord | undefined => {
      if (!prev) return prev;
      return Object.fromEntries(
        Object.entries(prev).map(([status, checklist]) => [
          status,
          updateChecklistDocuments(checklist) || checklist,
        ])
      );
    }
  );

  // Every window shows this document somewhere. Published from here rather than from the fifteen
  // call sites, so a mutation nobody thought about while writing it still crosses.
  const published = listDocument ?? graphDocument;
  if (published) {
    publishCrossWindowCacheChange({
      kind: "document",
      applicationId,
      documentId,
      document: toJS(published),
    });
  }
};

/**
 * Fold a document the server just handed back into the lists that show a deal's documents.
 *
 * `updateDocumentInFetchCache` rewrites a document a list already holds, which is the wrong shape
 * for an upload: a fresh file is in no list yet, and a re-upload of a file the deal already has
 * comes back RETYPED — the dedupe path retypes it server-side rather than storing the bytes twice.
 * Both arrive through `addFile`, which until now patched only the application graph, so the LOS
 * Documents tab — it reads `DOCUMENTS_KEY`, not the graph — kept showing the type the file had
 * before the upload until something else refetched it.
 *
 * Merged rather than replaced, because the documents endpoint hydrates more of a file than an
 * upload response does (the uploader, the robot's guess), and replacing would drop what the list
 * is already showing.
 */
export const addDocumentToFetchCache = (
  queryClient: QueryClient,
  applicationId: string,
  document: entities.UploadedFile
) => {
  if (!document?.id) return;

  queryClient.setQueriesData<entities.UploadedFile[]>(
    { exact: false, queryKey: [...DOCUMENTS_KEY, applicationId] },
    (prev) => {
      // A list this window never fetched stays unfetched: seeding it with the one document in hand
      // would have it render as the whole of the deal's documents.
      if (!prev) return prev;
      const index = prev.findIndex((doc) => doc.id === document.id);
      if (index === -1) return [document, ...prev];
      const next = [...prev];
      // Field by field rather than `{ ...previous, ...document }`, because an entity declares every
      // column as an own property initialised to `undefined` — spreading one writes those blanks
      // over everything the incoming copy happens not to carry, which for an upload response is
      // most of what the list is showing.
      const merged = new entities.UploadedFile({ ...prev[index] }) as Record<string, unknown>;
      for (const [field, value] of Object.entries(document)) {
        if (value !== undefined) merged[field] = value;
      }
      next[index] = merged as entities.UploadedFile;
      return next;
    }
  );
};

export const removeDocumentFromFetchCache = (
  queryClient: QueryClient,
  documentId: string,
  applicationId: string
) => {
  // 1. Remove from main documents list
  queryClient.setQueriesData<entities.UploadedFile[]>(
    { exact: false, queryKey: [...DOCUMENTS_KEY, applicationId] },
    (prev) => prev?.filter((doc) => doc.id !== documentId)
  );

  // 2. Remove from local-only documents cache
  queryClient.setQueriesData<entities.UploadedFile[]>(
    { exact: false, queryKey: [...DOCUMENTS_KEY, applicationId, "local"] },
    (prev) => prev?.filter((doc) => doc.id !== documentId)
  );

  // 3. Remove from documents needing approval
  queryClient.setQueriesData<entities.UploadedFile[]>(
    { exact: false, queryKey: ["documents", "needs-approval"] },
    (prev) => prev?.filter((doc) => doc.id !== documentId)
  );

  // 4. Remove individual file cache
  queryClient.removeQueries({ queryKey: ["initial-file", documentId] });

  // 5. Remove document from all nested application cache locations
  const filterFiles = (files: entities.UploadedFile[] | undefined | null) =>
    files?.filter((f) => f.id !== documentId);

  const filterFilesInOwnable = (ownable: { files?: entities.UploadedFile[] | null } | undefined | null) => {
    if (!ownable?.files) return ownable;
    return { ...ownable, files: filterFiles(ownable.files) };
  };

  const removeFromApp = (app: ChecklistApp): ChecklistApp => {
    return {
      ...app,
      files: filterFiles(app.files),
      applicants: app.applicants?.map((ap) => ({
        ...ap,
        files: filterFiles(ap?.files),
        incomes: ap?.incomes?.map((x) => filterFilesInOwnable(x)),
        debts: ap?.debts?.map((x) => filterFilesInOwnable(x)),
        assets: ap?.assets?.map((x) => filterFilesInOwnable(x)),
        properties: ap?.properties?.map((p) => ({
          ...p,
          debts: p?.debts?.map((x) => filterFilesInOwnable(x)),
          incomes: p?.incomes?.map((x) => filterFilesInOwnable(x)),
        })),
        homeAddress: ap?.homeAddress
          ? {
              ...ap.homeAddress,
              debts: ap.homeAddress?.debts?.map((x) => filterFilesInOwnable(x)),
              incomes: ap.homeAddress?.incomes?.map((x) => filterFilesInOwnable(x)),
            }
          : ap?.homeAddress,
      })),
      property: app.property
        ? {
            ...app.property,
            debts: app.property?.debts?.map((x) => filterFilesInOwnable(x)),
            incomes: app.property?.incomes?.map((x) => filterFilesInOwnable(x)),
          }
        : app.property,
    } as ChecklistApp;
  };

  const removeFromChecklistDocuments = (
    checklist: types.ChecklistResult[] | undefined
  ): types.ChecklistResult[] | undefined => {
    if (!checklist) return checklist;
    return checklist.map((item) => {
      if (!types.isDocumentChecklistItem(item)) return item;
      if (!item.documents?.some((doc) => doc.id === documentId)) return item;
      return {
        ...item,
        documents: item.documents?.filter((doc) => doc.id !== documentId),
      };
    });
  };

  updateApplicationInFetchCache(queryClient, applicationId, (prev) => {
    if (!prev) return prev;
    const updated = removeFromApp(prev);
    if (updated?.checklists) {
      return {
        ...updated,
        checklists: Object.fromEntries(
          Object.entries(updated.checklists).map(([status, checklist]) => [
            status,
            removeFromChecklistDocuments(checklist as types.ChecklistResult[]) || checklist,
          ])
        ),
      } as ChecklistApp;
    }
    return updated;
  });

  // 6. Update standalone checklist caches
  queryClient.setQueriesData<types.ChecklistResult[]>(
    { exact: false, queryKey: ["checklist", ...APPLICATION_KEY, applicationId] },
    (prev) => {
      if (!prev || !Array.isArray(prev)) return prev;
      return removeFromChecklistDocuments(prev);
    }
  );

  type ChecklistsRecord = Partial<Record<string, types.ChecklistResult[]>>;
  queryClient.setQueryData<ChecklistsRecord>(
    ["checklist", ...APPLICATION_KEY, applicationId],
    (prev): ChecklistsRecord | undefined => {
      if (!prev) return prev;
      return Object.fromEntries(
        Object.entries(prev).map(([status, checklist]) => [
          status,
          removeFromChecklistDocuments(checklist) || checklist,
        ])
      );
    }
  );

  publishCrossWindowCacheChange({ kind: "document-removed", applicationId, documentId });
};

/**
 * Read-only subscription to whatever's cached at the per-app queryKey. Never fetches and never
 * writes — implemented as a {@link useSyncExternalStore} subscriber to the QueryCache rather than
 * a {@link useQuery} observer so it can't interfere with the real fetcher
 * ({@link useApplicationAuthed} / {@link useApplicationQuery}), which share the same queryKey.
 *
 * History: a previous `useQuery`-based implementation seeded the shared cache entry with `null`
 * via `initialData` / a cache-reading `queryFn`, and combined with `refetchOnMount: false` that
 * masked the real fetch — observers like `useFileConnections` → `useApplicationEntities` saw a
 * permanently-empty app graph.
 */
export const useApplicationLocal = ({ id }: { id?: string }) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [...SINGLE_APPLICATION_KEY, id], [id]);

  const subscribe = useCallback(
    (notify: () => void) => {
      if (!id) return () => {};
      const cache = queryClient.getQueryCache();
      return cache.subscribe((event) => {
        const k = event.query.queryKey;
        if (Array.isArray(k) && k[0] === SINGLE_APPLICATION_KEY[0] && k[1] === id) notify();
      });
    },
    [queryClient, id]
  );

  const getSnapshot = useCallback(
    () => (id ? (queryClient.getQueryData<ChecklistApp>(queryKey) ?? null) : null),
    [queryClient, queryKey, id]
  );

  const data = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return useMemo(() => ({ data, isLoading: false as const }), [data]);
};

export function useApplicationAuthed<TSelected = ChecklistApp>({
  id,
  enabled = true,
  cached = true,
  refetch,
  initialRefetch,
  accountType,
  select,
}: {
  id?: string;
  enabled?: boolean;
  fetchEnabled?: boolean;
  cached?: boolean;
  refetch?: boolean;
  initialRefetch?: boolean;
  accountType?: UserAccountType;
  select?: (data: ChecklistApp) => TSelected;
}) {
  const { token } = useUpdateLocalUser();
  return useApplicationQuery<TSelected>({
    id,
    enabled: !!token && enabled,
    cached,
    refetch,
    initialRefetch,
    accountType,
    select,
  });
}

export const useApplication = ({
  id,
  enabled = true,
  fetchEnabled = true,
  cached,
  initialRefetch,
}: {
  id?: string;
  enabled?: boolean;
  fetchEnabled?: boolean;
  cached?: boolean;
  initialRefetch?: boolean;
}) => {
  const q = useApplicationQuery({ id, enabled: fetchEnabled, cached, initialRefetch });

  // Shared application mutations (web + mobile) — single source of truth in
  // @fraction/shared-frontend; optimistic writes go through the shared
  // updateApplicationInFetchCache so web's cache behavior is unchanged.
  const mutations = useSharedApplicationMutations({ id, enabled, getApp: () => q?.data });

  return {
    ...q,
    updateApplication: mutations.updateApplication.mutateAsync,
    updateApplicationProperty: mutations.updateApplicationProperty.mutateAsync,
    upsertApplicationSolicitor: mutations.upsertApplicationSolicitor,
    isPending:
      mutations.updateApplication.isPending ||
      mutations.updateApplicationProperty.isPending ||
      mutations.upsertApplicationSolicitor.isPending,
    rescindDeal: mutations.rescindDeal,
    reopenDeal: mutations.reopenDeal,
    rejectDeal: mutations.rejectDeal,
    approveDeal: mutations.approveDeal,
  };
};

/**
 * Returns only mutation functions for an application without subscribing to the query cache.
 * Use this in components that need mutations but don't need to read application data.
 */
export const useApplicationMutations = ({ id }: { id?: string }) => {
  const queryClient = useQueryClient();
  // Fully delegated to the shared factory in @fraction/shared-frontend; web's
  // single-app cache supplies the current application for derived data.
  return useSharedApplicationMutations({
    id,
    getApp: () => queryClient.getQueryData<ChecklistApp>([...SINGLE_APPLICATION_KEY, id]),
  });
};

export function useApplicationQuery<TSelected = ChecklistApp>({
  id,
  enabled = true,
  refetch = false,
  cached = true,
  initialData,
  placeholderData,
  initialRefetch,
  accountType,
  onSuccess,
  select,
  cacheKey,
  queryKey,
}: {
  id?: string;
  enabled?: boolean;
  refetch?: boolean;
  cached?: boolean;
  initialData?: ChecklistApp;
  placeholderData?: ChecklistApp;
  initialRefetch?: boolean;
  accountType?: UserAccountType;
  onSuccess?: (application?: ChecklistApp) => void;
  select?: (data: ChecklistApp) => TSelected;
  cacheKey?: string[];
  queryKey?: string[];
}) {
  const queryClient = useQueryClient();
  const stableSelect = useStableSelect(select ?? ((d: ChecklistApp) => d as unknown as TSelected));
  const effectiveSelect = select ? stableSelect : undefined;

  const effectiveQueryKey = useMemo(() => queryKey || [...SINGLE_APPLICATION_KEY, id], [queryKey, id]);

  // Show the barebones list row instantly while the full graph loads. The lite list seeds a
  // sibling slot (see `seedApplicationFromList`) rather than this detail slot, so it can only ever
  // arrive here as placeholder data — which never counts as real cached data, so the full fetch
  // still runs. Prefer the real slot (covers a custom `queryKey`), then fall back to the lite row.
  const placeholderFromCache = useCallback(() => {
    if (!id) return undefined;
    return (
      queryClient.getQueryData<ChecklistApp>([...SINGLE_APPLICATION_KEY, id]) ??
      queryClient.getQueryData<ChecklistApp>(liteApplicationKey(id))
    );
  }, [queryClient, id]);

  const result = (cached ? useCachedQuery : useQuery)<ChecklistApp, any, TSelected>({
    initialRefetch,
    initialData,
    placeholderData: placeholderData ?? placeholderFromCache,
    select: effectiveSelect as any,
    refetchOnMount: refetch,
    refetchOnWindowFocus: refetch,
    refetchOnReconnect: refetch,
    refetchInterval: refetch === false ? false : undefined,
    enabled: enabled && !!id,
    queryKey: effectiveQueryKey,
    cacheKey,
    queryFn: async () => {
      if (!id) {
        return null as any;
      }
      const result = await fraction.getApplication(id, { accountType });
      return result;
    },
    onSuccess: (application: any) => {
      if (application) {
        updateApplicationInFetchCache(queryClient, application.id, (_prevApp) => application);
        useSearchableAppsStore.getState().updateApp(application);
        onSuccess?.(application);
      }
    },
  });

  // Defensive self-heal: the detail slot is no longer seeded with lite data, but a returning user
  // can still have an `_isLite` app persisted into it by the old behaviour. Since `refetchOnMount`
  // is off for plain consumers, that stale seed would otherwise stick until a hard refresh. Force
  // one fetch when we detect it; GET /:id never returns `_isLite`, so this settles after one fetch
  // and can't loop. (Placeholder data lives in a sibling slot and never trips this.)
  const cachedAppIsLite =
    !!id && enabled && !!queryClient.getQueryData<ChecklistApp>(effectiveQueryKey)?._isLite;
  const refetchFn = result.refetch;
  useEffect(() => {
    if (cachedAppIsLite) {
      refetchFn();
    }
  }, [cachedAppIsLite, refetchFn]);

  if (!select && (result.data as any)?.id !== id) {
    return {
      ...result,
      data: undefined as TSelected | undefined,
    };
  }

  return result;
}

const LIMIT = 150;
const limits = splitNumberEqually(LIMIT, 8);
const limitsAndOffsets = limits.map((limit, index) => {
  return {
    limit,
    offset: limits.slice(0, index).reduce((acc, curr) => acc + curr, 0),
  };
});

const usePaginatedApplicationQuery = (
  {
    status = "active",
    accountType,
    enabled = true,
    queryKey,
    offset,
    limit,
    initialRefetch = true,
    lite = false,
    brand,
  }: {
    accountType?: "employee" | "broker" | "applicant" | "conveyancer" | "lender" | "desk-agent";
    status?: "active" | "all" | "closed" | "funded" | "realization" | "archived";
    enabled?: boolean;
    queryKey: (string | number | undefined)[];
    offset: number;
    limit: number;
    initialRefetch?: boolean;
    lite?: boolean;
    brand?: string;
  },
  options?: Partial<UseQueryOptions<any>>
) => {
  const cachedQueryClient = useQueryClient();
  const queryClient = useQueryClient();

  return useCachedQuery<types.ArrayResponse<ChecklistApp>>({
    initialRefetch,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: 2,
    ...options,
    enabled,
    queryKey,
    queryFn: async () => {
      return await fraction.getApplications({
        status,
        accountType,
        offset,
        limit,
        lite,
        brand,
      });
    },
    onSuccess: (apps, fromCache) => {
      const client = fromCache ? queryClient : cachedQueryClient;
      // List responses are always projections, so they only seed the sibling
      // placeholder slot and can never overwrite the full application graph.
      for (const app of apps?.data || []) {
        seedApplicationFromList(client, app);
      }
    },
  });
};

export const useInfiniteApplicationsQuery = (
  {
    status = "active",
    accountType,
    enabled = true,
    initialRefetch = true,
    liteEnabled = true,
    brand,
  }: {
    accountType?: "employee" | "broker" | "applicant" | "conveyancer" | "lender" | "desk-agent";
    status?: "active" | "all" | "closed" | "funded" | "realization" | "archived";
    enabled?: boolean;
    initialRefetch?: boolean;
    initialRefetchHeavy?: boolean;
    liteEnabled?: boolean;
    heavyEnabled?: boolean;
    /**
     * Brand pool for the broker pipeline. Part of the query key: switching brands must not
     * serve the other pool's cached pages.
     */
    brand?: string;
  } = {},
  options?: Partial<UseQueryOptions<any>>
) => {
  const { token } = useUpdateLocalUser();
  const cachedQueryClient = useQueryClient();

  const queryKey = [...APPLICATION_KEY, "infinite", status || "all", accountType, brand];

  useEffect(() => {
    return () => {
      cachedQueryClient.cancelQueries({ queryKey: [...APPLICATION_KEY, "infinite"], exact: false });
    };
  }, [cachedQueryClient]);
  const liteQueryKey = [...queryKey, "lite"];

  // Lite queries (fast, no checklists)
  const liteQ0 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[0].offset],
    offset: limitsAndOffsets[0].offset,
    brand,
    limit: limitsAndOffsets[0].limit,
    lite: true,
  });

  const liteQ1 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[1].offset],
    offset: limitsAndOffsets[1].offset,
    brand,
    limit: limitsAndOffsets[1].limit,
    lite: true,
  });

  const liteQ2 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[2].offset],
    offset: limitsAndOffsets[2].offset,
    brand,
    limit: limitsAndOffsets[2].limit,
    lite: true,
  });

  const liteQ3 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[3].offset],
    offset: limitsAndOffsets[3].offset,
    brand,
    limit: limitsAndOffsets[3].limit,
    lite: true,
  });

  const liteQ4 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[4].offset],
    offset: limitsAndOffsets[4].offset,
    brand,
    limit: limitsAndOffsets[4].limit,
    lite: true,
  });

  const liteQ5 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[5].offset],
    offset: limitsAndOffsets[5].offset,
    brand,
    limit: limitsAndOffsets[5].limit,
    lite: true,
  });

  const liteQ6 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[6].offset],
    offset: limitsAndOffsets[6].offset,
    brand,
    limit: limitsAndOffsets[6].limit,
    lite: true,
  });

  const liteQ7 = usePaginatedApplicationQuery({
    status,
    accountType,
    initialRefetch,
    enabled: liteEnabled && enabled && !!token,
    queryKey: [...liteQueryKey, limitsAndOffsets[7].offset],
    offset: limitsAndOffsets[7].offset,
    brand,
    limit: limitsAndOffsets[7].limit,
    lite: true,
  });

  // The employee pipeline "lite" query is now complete on its own (next-status
  // checklist via the cache join, plus comms/actions/loan), so there is no longer
  // a separate heavy/full fetch. Combine the lite chunks and stabilize the
  // reference when content is element-wise identical to the previous result, so
  // chunk-completion re-renders don't cascade into consumers (e.g. Pipeline) that
  // key heavy useMemos off this array's identity.
  const liteDataRef = useRef<ChecklistApp[]>([]);
  const liteData = useMemo(() => {
    const next = [
      ...(liteQ0.data?.data || []),
      ...(liteQ1.data?.data || []),
      ...(liteQ2.data?.data || []),
      ...(liteQ3.data?.data || []),
      ...(liteQ4.data?.data || []),
      ...(liteQ5.data?.data || []),
      ...(liteQ6.data?.data || []),
      ...(liteQ7.data?.data || []),
    ].filter(isSearchablePipelineApp);
    const prev = liteDataRef.current;
    if (prev.length === next.length && prev.every((p, i) => p === next[i])) {
      return prev;
    }
    liteDataRef.current = next;
    return next;
  }, [
    liteQ0.data?.data,
    liteQ1.data?.data,
    liteQ2.data?.data,
    liteQ3.data?.data,
    liteQ4.data?.data,
    liteQ5.data?.data,
    liteQ6.data?.data,
    liteQ7.data?.data,
  ]);

  const isLiteCacheLoading =
    liteQ0.isCacheLoading ||
    liteQ1.isCacheLoading ||
    liteQ2.isCacheLoading ||
    liteQ3.isCacheLoading ||
    liteQ4.isCacheLoading ||
    liteQ5.isCacheLoading ||
    liteQ6.isCacheLoading ||
    liteQ7.isCacheLoading;

  const isLiteFetching =
    liteQ0.isFetching ||
    liteQ1.isFetching ||
    liteQ2.isFetching ||
    liteQ3.isFetching ||
    liteQ4.isFetching ||
    liteQ5.isFetching ||
    liteQ6.isFetching ||
    liteQ7.isFetching;

  const isLiteLoading =
    liteQ0.isLoading ||
    liteQ1.isLoading ||
    liteQ2.isLoading ||
    liteQ3.isLoading ||
    liteQ4.isLoading ||
    liteQ5.isLoading ||
    liteQ6.isLoading ||
    liteQ7.isLoading;

  const combinedData = liteData;

  const q = {
    ...liteQ0,
    isLoading: isLiteLoading,
    isFetching: isLiteFetching,
    isCacheLoading: isLiteCacheLoading,
    isLiteFetching,
    isFullFetching: false,
    data: combinedData,
    count: liteQ0.data?.count,
  };

  const refetch = useCallback(() => {
    liteQ0.refetch();
    liteQ1.refetch();
    liteQ2.refetch();
    liteQ3.refetch();
    liteQ4.refetch();
    liteQ5.refetch();
    liteQ6.refetch();
    liteQ7.refetch();
  }, [
    liteQ0.refetch,
    liteQ1.refetch,
    liteQ2.refetch,
    liteQ3.refetch,
    liteQ4.refetch,
    liteQ5.refetch,
    liteQ6.refetch,
    liteQ7.refetch,
  ]);

  const dataUpdatedAt = useMemo(() => {
    return _.max([
      liteQ0.dataUpdatedAt,
      liteQ1.dataUpdatedAt,
      liteQ2.dataUpdatedAt,
      liteQ3.dataUpdatedAt,
      liteQ4.dataUpdatedAt,
      liteQ5.dataUpdatedAt,
      liteQ6.dataUpdatedAt,
      liteQ7.dataUpdatedAt,
    ]);
  }, [
    liteQ0.dataUpdatedAt,
    liteQ1.dataUpdatedAt,
    liteQ2.dataUpdatedAt,
    liteQ3.dataUpdatedAt,
    liteQ4.dataUpdatedAt,
    liteQ5.dataUpdatedAt,
    liteQ6.dataUpdatedAt,
    liteQ7.dataUpdatedAt,
  ]);

  // Keep the searchable apps index up-to-date for CommandPalette
  const updateSearchIndex = useSearchableAppsStore((s) => s.updateFromPipeline);
  useEffect(() => {
    if (combinedData.length > 0) {
      updateSearchIndex(combinedData);
    }
  }, [combinedData, updateSearchIndex]);

  return {
    data: combinedData,
    dataUpdatedAt,
    count: q.count,
    refetch: refetch,
    isPending: q.isPending,
    isFetching: q.isFetching,
    isLiteFetching: q.isLiteFetching,
    isFullFetching: q.isFullFetching,
  };
};

/**
 * Resolves the applicant's "current" application from a barebones lite list
 * (id + address + amount + status — see `fetchApplicationsForApplicantLite`),
 * then loads the full application through the shared single-application detail
 * cache (deduped against the chrome / checklist consumers, so exactly one detail
 * fetch). The lite list also powers the navbar `ApplicationSelector`.
 *
 * Selection: the navbar selector's explicit choice (persisted in
 * `useSelectedApplicationStore`) wins; otherwise we default to the
 * earliest-created application in scope — `_.minBy(apps, "createdAt")` — exactly
 * how the borrower app was chosen before.
 */
export const useActiveApplicantApplicationQuery = (
  { initialRefetch = true, status = "active" }: { initialRefetch?: boolean; status?: "active" | "closed" } = {
    initialRefetch: true,
    status: "active",
  }
) => {
  const selectedId = useSelectedApplicationStore((s) => s.selectedId);

  const liteQuery = useApplicationsQuery({
    accountType: "applicant",
    status,
    lite: true,
    initialRefetch,
  });
  const applications = liteQuery?.data;

  const defaultApp = useMemo(() => _.minBy(applications || [], "createdAt"), [applications]);

  // Prefer the persisted explicit choice. It's read synchronously from
  // localStorage, so on a refresh we can resolve the id (and load its cached
  // detail + checklist from the IndexedDB persister) immediately — without
  // waiting for the lite list to rehydrate/refetch. Once the lite list is in, we
  // drop the stored id if it's no longer in scope and fall back to the default
  // (earliest-created in-scope app — matching the legacy behaviour).
  const id = useMemo(() => {
    if (selectedId && (!applications || applications.some((app) => app.id === selectedId))) {
      return selectedId;
    }
    return defaultApp?.id;
  }, [selectedId, applications, defaultApp]);

  const appQuery = useApplicationAuthed({ id, initialRefetch, accountType: "applicant" });

  return {
    ...appQuery,
    app: appQuery?.data,
    id,
    // The lite list of in-scope applications, for the navbar selector.
    applications,
    // Gate only on resolving *which* application to load. Consumers that need the
    // full application read `app`/`data` (populated once the shared detail fetch
    // lands) and the chrome drives its own detail-loading skeleton.
    isLoadingApp: appQuery.isLoading,
    isLoading: liteQuery.isLoading,
  };
};

type GetApplicationsResult = Awaited<ReturnType<typeof fraction.getApplications>>;

export const useApplicationsQuery = (
  {
    status = "active",
    accountType,
    enabled = true,
    initialRefetch = true,
    countOnly,
    lite,
  }: {
    accountType?: "employee" | "broker" | "applicant" | "conveyancer" | "desk-agent";
    status?: "active" | "all" | "closed";
    enabled?: boolean;
    refetch?: boolean;
    initialRefetch?: boolean;
    countOnly?: boolean;
    lite?: boolean;
  } = {},
  options?: Partial<UseQueryOptions<any>>
) => {
  const { token } = useUpdateLocalUser();
  const queryClient = useQueryClient();

  // `lite` is part of the key so a barebones list never shares a cache slot with
  // the full graph for the same status/accountType.
  const queryKey = countOnly
    ? ["count", status || "all", accountType]
    : [...APPLICATION_KEY, status || "all", accountType, ...(lite ? ["lite"] : [])];

  const q = useCachedQuery<GetApplicationsResult, any, GetApplicationsResult>({
    initialRefetch,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    ...options,
    enabled: enabled && !!token,
    queryKey,
    queryFn: async () => {
      const apps = await fraction.getApplications({
        status,
        accountType,
        countOnly,
        lite,
      });

      for (const app of apps?.data || []) {
        // List rows land in the placeholder slot; the detail endpoint remains authoritative.
        seedApplicationFromList(queryClient, app);

        if (accountType === "applicant") {
          queryClient.setQueriesData<ChecklistApp>(
            {
              exact: false,
              queryKey: [...SINGLE_APPLICATION_KEY, `ApplicantApplication`, status],
            },
            (prev?: ChecklistApp | undefined) => {
              if (prev?.id === app?.id) {
                return liteMerge(prev, app);
              }
              return prev;
            }
          );
        }
      }

      return apps;
    },
  });

  return {
    ...q,
    data: q?.data?.data,
    count: q?.data?.count,
    countAll: q?.data?.all,
    countActive: q?.data?.active,
    countClosed: q?.data?.closed,
    countFunded: q?.data?.funded,
    isLoading: q.isLoading || q.isCacheLoading,
  };
};

function splitNumberEqually(number: number, parts: number): number[] {
  const quotient = Math.floor(number / parts);
  const remainder = number % parts;

  const result = new Array(parts).fill(quotient);

  for (let i = 0; i < remainder; i++) {
    result[i]++;
  }

  return result;
}

// liteApplicationKey / seedApplicationFromList (and its liteMerge) moved to
// @fraction/shared-frontend so mobile's shared useApplication/useApplications
// get the same poison-free lite seeding; imported + re-exported above.
